/**
 * Inkwell — printer finder component.
 * Owner: Agent B. Paired with sections/printer-finder.liquid.
 *
 * Loaded with `defer` from the section. It used to be inlined in the section,
 * which cost ~21.6KB of uncacheable, parse-blocking HTML on every page that
 * carried a finder -- twice on a page with two instances. The per-section
 * printer JSON payload stays inline; only this behaviour moved out.
 *
 * Deferred execution is also safer than the old inline placement: the element's
 * children are guaranteed to exist by the time customElements.define() upgrades
 * it, so the parse-time trap documented in BUILD_STATUS 3.9 cannot come back.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('printer-finder')) return;

  var DEBOUNCE_LOCAL = 90;
  var DEBOUNCE_REMOTE = 260;
  var MAX_LOCAL = 8;
  var MAX_REMOTE = 6;

  var dataCache = new WeakMap();

  function isVisibleEl(el) {
    if (!el || !el.isConnected) return false;
    if (el.getClientRects().length === 0) return false;
    var style = window.getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none';
  }

  /**
   * A page can hold more than one finder (home hero + mega menu), and a
   * duplicated id is invalid HTML -- the browser would resolve the fragment
   * to whichever came first, which may be the collapsed mega menu instance.
   * Keep the id on exactly one element: the first visible instance that opted
   * in, preferring an opted-in one, falling back to any visible one.
   */
  function resolveAnchor() {
    var nodes = Array.prototype.slice.call(
      document.querySelectorAll('[data-printer-finder-anchor]')
    );
    // Single instance: whatever Liquid rendered is already correct.
    if (nodes.length < 2) return;

    var optedIn = function (n) {
      return n.getAttribute('data-printer-finder-anchor') === '1';
    };
    var visible = nodes.filter(isVisibleEl);
    var winner =
      visible.filter(optedIn)[0] || visible[0] || nodes.filter(optedIn)[0] || nodes[0];

    nodes.forEach(function (node) {
      if (node !== winner && node.id === 'printer-finder') node.removeAttribute('id');
    });
    if (winner.id !== 'printer-finder') winner.id = 'printer-finder';
  }

  // This file only ever runs once per page (guarded by customElements above),
  // so no extra once-flag is needed.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resolveAnchor, { once: true });
  } else {
    resolveAnchor();
  }

  function readData(root) {
    var script =
      root.querySelector('script[data-printer-finder-data]') ||
      document.querySelector('script[data-printer-finder-data]');
    if (!script) return { brands: [], models: [] };
    if (dataCache.has(script)) return dataCache.get(script);
    var parsed = { brands: [], models: [] };
    try {
      parsed = JSON.parse(script.textContent) || parsed;
    } catch (e) {
      parsed = { brands: [], models: [] };
    }
    if (!Array.isArray(parsed.brands)) parsed.brands = [];
    if (!Array.isArray(parsed.models)) parsed.models = [];
    dataCache.set(script, parsed);
    return parsed;
  }

  function norm(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[\s\-_/]+/g, '');
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function plural(count, word) {
    return count + ' ' + word + (count === 1 ? '' : 's');
  }

  function PrinterFinderClass() {
    return class PrinterFinder extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this.uid = this.dataset.uid || String(Math.random()).slice(2);
        this.urlPrefix = this.dataset.modelUrlPrefix || '/collections/';
        this.remember = this.dataset.remember !== 'false';
        this.predictive =
          this.dataset.predictiveEnabled !== 'false' && !!this.dataset.predictiveUrl;
        this.hasModels = this.dataset.hasModels === 'true';

        // Single live region for the whole component. It sits outside both tab
        // panels on purpose: a region inside a `hidden` panel is not in the
        // accessibility tree, so its updates would never reach a screen reader.
        this.live = this.querySelector('[data-pf-live]');

        this.data = this.hasModels ? readData(this) : { brands: [], models: [] };
        this.buildIndex();

        this.setupTabs();
        this.setupSelects();
        this.setupCombobox();

        this.setAttribute('data-enhanced', 'true');
      }

      /* ---------- announcements ---------- */

      announce(message) {
        if (!this.live || !message) return;
        // Identical consecutive text is not re-announced by most screen readers;
        // clearing first makes the repeat land.
        if (this.live.textContent === message) this.live.textContent = '';
        this.live.textContent = message;
      }

      /* ---------- indexing ---------- */

      buildIndex() {
        var models = this.data.models || [];
        var brandNames = {};
        (this.data.brands || []).forEach(function (b) {
          if (b && b.h) brandNames[b.h] = b.n || b.h;
        });

        var byBrand = {};
        var order = [];

        models.forEach(function (m) {
          if (!m || !m.h) return;
          var bh = m.b || 'other';
          if (!brandNames[bh]) brandNames[bh] = m.bn || bh;
          if (!byBrand[bh]) {
            byBrand[bh] = {};
            order.push(bh);
          }
          var sk = m.s || '';
          if (!byBrand[bh][sk]) byBrand[bh][sk] = [];
          byBrand[bh][sk].push(m);
        });

        this.brandNames = brandNames;
        this.byBrand = byBrand;
        // Brands declared in the metaobject keep their authored order; any
        // brand seen only on a model is appended afterwards.
        var declared = (this.data.brands || [])
          .map(function (b) {
            return b.h;
          })
          .filter(function (h) {
            return !!byBrand[h];
          });
        var extra = order.filter(function (h) {
          return declared.indexOf(h) === -1;
        });
        this.brandOrder = declared.concat(extra);
      }

      /* ---------- tabs ---------- */

      setupTabs() {
        var self = this;
        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
        if (!this.tabs.length) return;

        this.tabs.forEach(function (tab, i) {
          tab.addEventListener('click', function () {
            self.selectTab(i);
          });
          tab.addEventListener('keydown', function (event) {
            var key = event.key;
            var next = null;
            if (key === 'ArrowRight' || key === 'ArrowDown') next = (i + 1) % self.tabs.length;
            else if (key === 'ArrowLeft' || key === 'ArrowUp')
              next = (i - 1 + self.tabs.length) % self.tabs.length;
            else if (key === 'Home') next = 0;
            else if (key === 'End') next = self.tabs.length - 1;
            if (next === null) return;
            event.preventDefault();
            self.selectTab(next);
            self.tabs[next].focus();
          });
        });
      }

      selectTab(index) {
        var self = this;
        this.tabs.forEach(function (tab, i) {
          var active = i === index;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.setAttribute('tabindex', active ? '0' : '-1');
          tab.classList.toggle('is-active', active);
          var panel = self.querySelector('#' + tab.getAttribute('aria-controls'));
          if (!panel) return;
          panel.hidden = !active;
          panel.classList.toggle('is-hidden', !active);
        });
      }

      /* ---------- cascading selects ---------- */

      setupSelects() {
        var self = this;
        this.brandSelect = this.querySelector('[data-pf-select="brand"]');
        this.seriesSelect = this.querySelector('[data-pf-select="series"]');
        this.modelSelect = this.querySelector('[data-pf-select="model"]');
        this.selectForm = this.querySelector('[data-pf-form="select"]');
        if (!this.brandSelect || !this.seriesSelect || !this.modelSelect) return;

        this.submitButton = this.selectForm
          ? this.selectForm.querySelector('.printer-finder__submit')
          : null;
        this.submitLabel = this.submitButton
          ? this.submitButton.textContent.trim()
          : 'Find my cartridges';

        // Rebuild the brand list so brands that exist only on models show up too.
        this.fill(this.brandSelect, 'Select brand', this.brandOrder.map(function (h) {
          return { value: h, label: self.brandNames[h] || h };
        }));

        this.brandSelect.addEventListener('change', function () {
          self.onBrandChange();
        });
        this.seriesSelect.addEventListener('change', function () {
          self.onSeriesChange();
        });
        this.modelSelect.addEventListener('change', function () {
          self.syncSubmitState(true);
        });

        if (this.selectForm) {
          this.selectForm.addEventListener('submit', function (event) {
            event.preventDefault();
            self.submitSelected();
          });
        }

        this.syncSubmitState();
      }

      fill(select, placeholder, items) {
        var fragment = document.createDocumentFragment();
        var first = document.createElement('option');
        first.value = '';
        first.textContent = placeholder;
        fragment.appendChild(first);
        items.forEach(function (item) {
          var option = document.createElement('option');
          option.value = item.value;
          option.textContent = item.label;
          fragment.appendChild(option);
        });
        select.innerHTML = '';
        select.appendChild(fragment);
        select.disabled = items.length === 0;
        return items.length;
      }

      onBrandChange() {
        var self = this;
        var brand = this.brandSelect.value;
        var groups = this.byBrand[brand] || {};
        var keys = Object.keys(groups).sort(function (a, b) {
          return a.localeCompare(b);
        });

        if (!brand) {
          this.fill(this.seriesSelect, 'Select series', []);
          this.fill(this.modelSelect, 'Select model', []);
          this.syncSubmitState();
          this.announce('Brand cleared. Series and model lists are unavailable.');
          return;
        }

        // A brand whose models carry no series value skips straight to models.
        if (keys.length === 1 && keys[0] === '') {
          this.fill(this.seriesSelect, 'Not applicable', []);
          var direct = this.fillModels(groups['']);
          this.announce(
            'No series for this brand. Model list ready, ' + plural(direct, 'option') + '.'
          );
        } else {
          var count = this.fill(
            this.seriesSelect,
            'Select series',
            keys.map(function (k) {
              return { value: k, label: k || 'Other' };
            })
          );
          this.fill(this.modelSelect, 'Select model', []);
          this.announce('Series list ready, ' + plural(count, 'option') + '.');
        }
        this.syncSubmitState();
      }

      onSeriesChange() {
        var groups = this.byBrand[this.brandSelect.value] || {};
        var series = this.seriesSelect.value;
        var count = this.fillModels(series ? groups[series] : []);
        this.syncSubmitState();
        this.announce(
          series
            ? 'Model list ready, ' + plural(count, 'option') + '.'
            : 'Series cleared. Model list is unavailable.'
        );
      }

      fillModels(list) {
        var models = (list || []).slice().sort(function (a, b) {
          return String(a.n).localeCompare(String(b.n), undefined, { numeric: true });
        });
        var count = this.fill(
          this.modelSelect,
          'Select model',
          models.map(function (m) {
            return { value: m.h, label: m.n };
          })
        );
        this._modelLookup = {};
        var lookup = this._modelLookup;
        models.forEach(function (m) {
          lookup[m.h] = m;
        });
        return count;
      }

      /**
       * @param {boolean} [speak] announce the button flipping to available.
       *   Only the model <select> passes true; the initial call stays silent.
       */
      syncSubmitState(speak) {
        if (!this.submitButton) return;
        var was = this.submitButton.disabled;
        var now = !this.modelSelect.value;
        this.submitButton.disabled = now;
        if (speak && was && !now) {
          this.announce(this.submitLabel + ' is now available.');
        }
      }

      submitSelected() {
        var handle = this.modelSelect.value;
        if (!handle) return;
        var model = (this._modelLookup || {})[handle];
        var selected = this.modelSelect.options[this.modelSelect.selectedIndex];
        this.go(model || { h: handle, n: selected ? selected.textContent : handle });
      }

      /* ---------- type-ahead ---------- */

      setupCombobox() {
        var self = this;
        this.input = this.querySelector('.printer-finder__input');
        this.listbox = this.querySelector('.printer-finder__listbox');
        this.empty = this.querySelector('[data-pf-empty]');
        this.typeForm = this.querySelector('[data-pf-form="type"]');
        if (!this.input || !this.listbox) return;

        this.options = [];
        this.activeIndex = -1;

        var runLocal = debounce(function () {
          self.renderSuggestions(self.localMatches(self.input.value));
        }, DEBOUNCE_LOCAL);

        var runRemote = debounce(function () {
          self.fetchRemote(self.input.value);
        }, DEBOUNCE_REMOTE);

        this.input.addEventListener('input', function () {
          self._remote = [];
          runLocal();
          if (self.predictive) runRemote();
        });

        this.input.addEventListener('keydown', function (event) {
          self.onComboKeydown(event);
        });

        this.input.addEventListener('focus', function () {
          if (self.input.value.trim()) runLocal();
        });

        document.addEventListener('click', function (event) {
          if (!self.contains(event.target)) self.closeList();
        });

        if (this.typeForm) {
          this.typeForm.addEventListener('submit', function (event) {
            if (self.activeIndex >= 0 && self.options[self.activeIndex]) {
              event.preventDefault();
              self.choose(self.options[self.activeIndex]);
              return;
            }
            // Exactly one confident local match: jump straight to the model page.
            var matches = self.localMatches(self.input.value);
            if (matches.length === 1) {
              event.preventDefault();
              self.choose(matches[0]);
            }
            // Otherwise fall through to the native /search?q= submit.
          });
        }
      }

      localMatches(query) {
        var q = norm(query);
        if (q.length < 2) return [];
        var models = this.data.models || [];
        var starts = [];
        var contains = [];
        for (var i = 0; i < models.length; i++) {
          var m = models[i];
          var hay = norm(m.n) + norm(m.bn) + norm(m.s);
          var name = norm(m.n);
          if (name.indexOf(q) === 0) starts.push(m);
          else if (hay.indexOf(q) !== -1) contains.push(m);
          if (starts.length >= MAX_LOCAL) break;
        }
        return starts
          .concat(contains)
          .slice(0, MAX_LOCAL)
          .map(function (m) {
            return {
              kind: 'model',
              label: m.n,
              meta: [m.bn, m.s].filter(Boolean).join(' · '),
              model: m,
            };
          });
      }

      fetchRemote(query) {
        var self = this;
        var q = String(query || '').trim();
        if (q.length < 2) return;
        var url =
          this.dataset.predictiveUrl +
          '.json?q=' +
          encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=' +
          MAX_REMOTE;

        if (this._abort && this._abort.abort) this._abort.abort();
        this._abort = typeof AbortController !== 'undefined' ? new AbortController() : null;

        fetch(url, this._abort ? { signal: this._abort.signal } : undefined)
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .then(function (json) {
            if (!json || !json.resources || !json.resources.results) return;
            var products = json.resources.results.products || [];
            self._remote = products.slice(0, MAX_REMOTE).map(function (p) {
              return {
                kind: 'product',
                label: p.title,
                meta: p.vendor || '',
                url: p.url,
              };
            });
            self.renderSuggestions(self.localMatches(self.input.value));
          })
          .catch(function () {
            /* aborted or offline: local matches still render */
          });
      }

      showEmpty(message) {
        if (!this.empty) return;
        this.empty.textContent = message;
        this.empty.hidden = false;
      }

      hideEmpty() {
        if (this.empty) this.empty.hidden = true;
      }

      renderSuggestions(local) {
        var remote = this._remote || [];
        var all = local.concat(remote);
        this.options = all;
        this.activeIndex = -1;
        this.listbox.innerHTML = '';

        if (!all.length) {
          // Predictive search can be unavailable (offline, or the storefront is
          // password protected). Say so rather than showing an empty box.
          this.listbox.hidden = true;
          this.input.setAttribute('aria-expanded', 'false');
          this.input.removeAttribute('aria-activedescendant');
          if (String(this.input.value).trim().length < 2) {
            this.hideEmpty();
            this.announce('');
            return;
          }
          // The message lives outside the listbox: a listbox may only own
          // `option` and `group` children, so a presentational <li> would be
          // dropped from the accessibility tree anyway.
          this.showEmpty('No printer matched. Press Enter to search everything.');
          this.announce('No suggestions.');
          return;
        }

        this.hideEmpty();

        var self = this;
        var lastKind = null;
        var currentList = null;
        var groupIndex = 0;
        all.forEach(function (item, i) {
          if (item.kind !== lastKind) {
            lastKind = item.kind;
            // ARIA only allows `option` and `group` as listbox children, so the
            // category header is a real group labelled by its visible caption.
            // Without this the "Your printer" / "Cartridges" split -- the only
            // cue that one route is a model landing page and the other a
            // product -- is invisible to screen readers.
            var headId = 'PfGrpLabel-' + self.uid + '-' + groupIndex++;
            var group = document.createElement('li');
            group.className = 'printer-finder__group';
            group.setAttribute('role', 'group');
            group.setAttribute('aria-labelledby', headId);

            var caption = document.createElement('span');
            caption.className = 'printer-finder__group-label ink-label';
            caption.id = headId;
            caption.textContent = item.kind === 'model' ? 'Your printer' : 'Cartridges';
            group.appendChild(caption);

            currentList = document.createElement('ul');
            currentList.className = 'printer-finder__group-list';
            // role=none keeps the inner list transparent so the options stay
            // owned by the group above it.
            currentList.setAttribute('role', 'none');
            group.appendChild(currentList);

            self.listbox.appendChild(group);
          }
          var li = document.createElement('li');
          li.className = 'printer-finder__option';
          li.id = 'PfOpt-' + self.uid + '-' + i;
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', 'false');
          li.dataset.index = String(i);

          var label = document.createElement('span');
          label.className = 'printer-finder__option-label';
          label.textContent = item.label;
          li.appendChild(label);

          if (item.meta) {
            var meta = document.createElement('span');
            meta.className = 'printer-finder__option-meta';
            meta.textContent = item.meta;
            li.appendChild(meta);
          }

          li.addEventListener('mousedown', function (event) {
            event.preventDefault();
            self.choose(item);
          });
          currentList.appendChild(li);
        });

        this.listbox.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.announce(plural(all.length, 'suggestion'));
      }

      closeList() {
        if (!this.listbox) return;
        this.listbox.hidden = true;
        this.hideEmpty();
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.activeIndex = -1;
      }

      onComboKeydown(event) {
        if (event.key === 'Escape') {
          this.closeList();
          return;
        }
        if (!this.options.length || this.listbox.hidden) return;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          var delta = event.key === 'ArrowDown' ? 1 : -1;
          var next = this.activeIndex + delta;
          if (next < 0) next = this.options.length - 1;
          if (next >= this.options.length) next = 0;
          this.setActive(next);
        } else if (event.key === 'Enter' && this.activeIndex >= 0) {
          event.preventDefault();
          this.choose(this.options[this.activeIndex]);
        }
      }

      setActive(index) {
        var nodes = this.listbox.querySelectorAll('[role="option"]');
        for (var i = 0; i < nodes.length; i++) {
          var on = Number(nodes[i].dataset.index) === index;
          nodes[i].classList.toggle('is-active', on);
          nodes[i].setAttribute('aria-selected', on ? 'true' : 'false');
          if (on) {
            this.input.setAttribute('aria-activedescendant', nodes[i].id);
            if (nodes[i].scrollIntoView) nodes[i].scrollIntoView({ block: 'nearest' });
          }
        }
        this.activeIndex = index;
      }

      choose(item) {
        this.closeList();
        if (item.kind === 'product' && item.url) {
          window.location.href = item.url;
          return;
        }
        this.go(item.model || item);
      }

      /* ---------- navigation + context ---------- */

      go(model) {
        if (!model || !model.h) return;
        // Model landing pages are only built for the top models. When the
        // section verified the collection and it does not exist, send the
        // shopper to search rather than a 404.
        var missingPage = this.data.verified === true && model.c === 0;
        if (this.remember && window.InkwellPrinter && window.InkwellPrinter.set) {
          window.InkwellPrinter.set({
            handle: model.h,
            displayName: model.n || model.h,
            brand: model.bn || model.b || '',
            series: model.s || '',
          });
        }
        if (missingPage) {
          window.location.href =
            (this.dataset.searchUrl || '/search') +
            '?q=' +
            encodeURIComponent(model.n || model.h) +
            '&options%5Bprefix%5D=last';
          return;
        }
        window.location.href = this.urlPrefix + encodeURIComponent(model.h);
      }
    };
  }

  if (window.customElements) {
    customElements.define('printer-finder', PrinterFinderClass());
  }
})();
